'use client';

import Image from 'next/image';
import Link from 'next/link';
import Button from './Button';

type CardItem = {
  letter: string;
  label: string;
  icon?: string;
  imageSrc?: string;
  para?: string;
  href?: string;
};

type ButtonProps = {
  buttontext?: string;
  variant?: 'primary' | 'secondary';
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'xl2';
  href?: string;
  onClick?: () => void;
  fullWidth?: boolean;
};

type ZigZagCardsSectionProps = {
  title?: string;
  subtitle?: string;
  description?: string;
  highlightRed?: boolean;
  align?: 'left' | 'center' | 'right';
  cards?: CardItem[];
  headerImageSrc?: string;
  button?: ButtonProps;
};

const alphabetData: CardItem[] = [
  { letter: 'Design/Develop', label: '-as-a-Service', icon: '/icons/Section-Icons/HomeIcons/DesignDevelop.svg', href: '/design-development-as-a-service' },
  { letter: 'AI', label: '-as-a-Service', icon: '/icons/Section-Icons/HomeIcons/AI.svg', href: '/ai-as-a-service' },
  { letter: 'Growth', label: '-as-a-Service', icon: '/icons/Section-Icons/HomeIcons/Growth.svg', href: '/growth-as-a-service' },
  { letter: 'Staff', label: '-as-a-Service', icon: '/icons/Section-Icons/HomeIcons/StaffIcon.svg', href: '/staff-as-a-service' },
];

const ZigZagCardsSection = ({
  title,
  subtitle,
  description,
  cards,
  headerImageSrc,
  button,
}: ZigZagCardsSectionProps) => {
  const cardsData = cards ?? alphabetData;

  return (
    <section className="custom-container  sm:mt-0 py-12 lg:py-16 xl:py-20 2xl:py-24 overflow-hidden relative">


      <div className="flex flex-col 2xl:flex-row items-center md:gap-28  2xl:gap-0 gap-8">
        {/* Left Text Section */}
        <div className="w-full flex items-end justify-between 2xl:w-1/2 2xl:flex-col 2xl:items-start">
          <div>
            <div className="flex flex-col relative z-10 pb-4 ">
              {subtitle && (
                <h4 className="xl:text-4xl text-xl md:text-2xl text-[#1b1b1b] font-semibold tracking-tight pb-2">{subtitle}</h4>
              )}

              {headerImageSrc && (
                <Image
                  src={headerImageSrc}
                  alt={`${title ?? 'header'} image`}
                  width={600}
                  height={70}
                  className="w-100  lg:w-120 xl:w-140 object-contain rounded-md"
                />
              )}

              {title && <h2 className="md:text-4xl lg:text-5xl text-3xl font-semibold tracking-tight text-[#272d57]">{title}</h2>}
            </div>

            {description && (
              <p className="relative z-10 lg:text-xl text-[#1b1b1b]  max-w-xl">{description}</p>
            )}
          </div>

          {/* Button shown only on xl and above */}
          {button?.buttontext && (
            <div className="hidden xl:block ml-4 mt-4">
              <Button
                variant={button.variant ?? 'primary'}
                size={button.size ?? 'md'}
                {...(button.href && { href: button.href })}
                {...(button.onClick && { onClick: button.onClick })}
                {...(button.fullWidth && { fullWidth: button.fullWidth })}
              >
                {button.buttontext}
              </Button>
            </div>
          )}
        </div>

        {/* Right Cards Section */}
        <div className="w-full 2xl:w-1/2 relative 2xl:py-12 w-full">
          <div className="grid grid-cols-2 lg:grid-cols-4 2xl:grid-cols-2 gap-4 w-full relative z-10">
            {cardsData.map((card, index) => (
              <Link
                key={index}
                href={card.href || '#'}
                className={`relative group overflow-hidden cursor-pointer flex flex-col bg-[#fff]/10 backdrop-blur-2xl 
                  hover:border-[#d53333]/50 
                  justify-center items-center text-center border border-[#C5C5C5]/20 
                  hover:shadow-[0_10px_40px_-15px_rgba(213,51,51,0.3)] transition-all duration-300 p-4 sm:p-5 md:p-6 
                 lg: min-h-[160px] 2xl:min-h-[280px] 
                  ${index === 0 || index === 2 ? 'sm:-translate-y-[64px]' : ''}`}
                onMouseMove={(e) => {
                  const card = e.currentTarget;
                  const rect = card.getBoundingClientRect();
                  const x = e.clientX - rect.left;
                  const y = e.clientY - rect.top;

                  const blurCircle = card.querySelector('.blur-circle') as HTMLElement;
                  if (blurCircle) {
                    blurCircle.style.left = `${x}px`;
                    blurCircle.style.top = `${y}px`;
                  }
                }}
                onMouseEnter={(e) => {
                  const card = e.currentTarget;
                  const blurCircle = card.querySelector('.blur-circle') as HTMLElement;
                  if (blurCircle) {
                    blurCircle.style.opacity = '1';
                  }
                }}
                onMouseLeave={(e) => {
                  const card = e.currentTarget;
                  const blurCircle = card.querySelector('.blur-circle') as HTMLElement;
                  if (blurCircle) {
                    blurCircle.style.opacity = '0';
                  }
                }}
              >
                {/* Blur circle that follows cursor */}
                <div
                  className="blur-circle absolute w-28 h-28 bg-[#d53333]/20 rounded-full blur-xl pointer-events-none transition-opacity duration-300 opacity-0 -translate-x-24 -translate-y-24 z-0"
                  style={{
                    left: '50%',
                    top: '50%',
                  }}
                />

                <div className="relative z-10 flex flex-col justify-center items-center text-center">
                  {card.imageSrc ? (
                    <Image
                      src={card.imageSrc}
                      alt={`${card.letter} image`}
                      width={128}
                      height={128}
                      className="w-full h-20 lg:h-28 xl:h-32 object-cover rounded-md mb-3"
                    />
                  ) : card.icon ? (
                    <div className="w-10 lg:w-14 h-10 lg:h-14 mb-1 lg:mb-3">
                      <Image src={card.icon} alt={`${card.letter} icon`} width={56} height={56} className="w-14 h-14" />
                    </div>
                  ) : null}

                  <h4 className="font-semibold text-base lg:text-xl mt-4 text-[#1b1b1b] leading-none">{card.letter}</h4>
                  <p className="mt-2 text-base lg:text-xl font-thin text-[#272d57] leading-snug">{card.label}</p>

                  {card.para && <p className="mt-2 text-base    font-thin text-[#272d57] leading-snug">{card.para}</p>}
                </div>
              </Link>
            ))}
          </div>

          {/* Button shown only below xl (under cards) */}
          {button?.buttontext && (
            <div className="flex justify-center xl:hidden mt-8">
              <Button
                variant={button.variant ?? 'primary'}
                size={button.size ?? 'md'}
                {...(button.href && { href: button.href })}
                {...(button.onClick && { onClick: button.onClick })}
              >
                {button.buttontext}
              </Button>
            </div>
          )}

        </div>
      </div>
    </section>
  );
};

export default ZigZagCardsSection;
